import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Users, Activity, TrendingUp, Shield, Brain, Trophy, ShieldCheck } from "lucide-react";

import showcaseSquad from "@/assets/showcase-squad.jpg";
import showcasePerformance from "@/assets/showcase-performance.jpg";
import showcaseWorkload from "@/assets/showcase-workload.jpg";
import showcaseMedical from "@/assets/showcase-medical.jpg";
import showcaseInsights from "@/assets/showcase-insights.png";
import showcaseMatchday from "@/assets/showcase-matchday.png";
import showcaseGovernance from "@/assets/showcase-governance.png";

const features = [
  {
    id: "squad",
    label: "Squad",
    icon: Users,
    heading: "Manage your entire squad in one place",
    pills: ["Player profiles", "Availability", "Status tracking"],
    image: showcaseSquad,
  },
  {
    id: "performance",
    label: "Performance",
    icon: TrendingUp,
    heading: "Unified performance data across every metric",
    pills: ["GPS & speed", "Strength testing", "Player comparison"],
    image: showcasePerformance,
  },
  {
    id: "workload",
    label: "Workload",
    icon: Activity,
    heading: "Monitor load and catch injury risk early",
    pills: ["ACWR tracking", "Risk flags", "Training load trends"],
    image: showcaseWorkload,
  },
  {
    id: "medical",
    label: "Medical",
    icon: Shield,
    heading: "Full medical governance and injury audit trail",
    pills: ["Return-to-play", "Concussion protocols", "Medical history"],
    image: showcaseMedical,
  },
  {
    id: "insights",
    label: "AI Insights",
    icon: Brain,
    heading: "AI briefings that turn data into decisions",
    pills: ["Daily briefings", "Readiness forecast", "Scenario planning"],
    image: showcaseInsights,
  },
  {
    id: "matchday",
    label: "Match Day",
    icon: Trophy,
    heading: "Match preparation with full squad intelligence",
    pills: ["Lineup builder", "Opposition analysis", "Fixture management"],
    image: showcaseMatchday,
  },
  {
    id: "governance",
    label: "Governance",
    icon: ShieldCheck,
    heading: "Guardrails and role-based access that protect your athletes",
    pills: ["Decision guardrails", "User roles & permissions", "Audit trail", "Risk acknowledgment"],
    image: showcaseGovernance,
  },
];

export function FeatureShowcase() {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = features[activeIndex];

  return (
    <section className="py-16 md:py-24">
      <div className="container mx-auto px-4">
        {/* Tab bar */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex flex-wrap justify-center gap-1 border-b border-border">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <button
                  key={f.id}
                  onClick={() => setActiveIndex(i)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative",
                    i === activeIndex
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{f.label}</span>
                  {i === activeIndex && (
                    <motion.div
                      layoutId="showcase-underline"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                      transition={{ type: "spring", stiffness: 500, damping: 35 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content area */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.3 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-5">
              {active.heading}
            </h2>

            {/* Feature pills */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {active.pills.map((pill) => (
                <span
                  key={pill}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card text-sm text-muted-foreground"
                >
                  {pill}
                </span>
              ))}
            </div>

          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
